import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { 
  Plus, Edit2, Trash2, Search, ChevronLeft, ChevronRight, 
  Star, AlertCircle, CheckCircle, Loader2, Layers, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LifecycleStageManagement() {
  const { token } = useAuth();
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedStage, setSelectedStage] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    sort_order: 0,
    is_active: true
  });

  // Sync from HubSpot
  const syncFromHubSpot = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/integrations/hubspot/sync-master-values?token=${token}`);
      if (response.data.success) {
        const stageStats = response.data.lifecycle_stages || {};
        toast.success(`HubSpot sync complete: ${stageStats.created || 0} created, ${stageStats.updated || 0} updated`);
        fetchStages();
      } else {
        toast.error('Failed to sync from HubSpot');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to sync from HubSpot';
      toast.error(message);
    } finally {
      setSyncing(false);
    }
  };

  const fetchStages = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        token,
        page: page.toString(),
        limit: '20',
        include_inactive: includeInactive.toString()
      });
      if (search) params.append('search', search);
      
      const response = await axios.get(`${API}/master/lifecycle-stages?${params}`);
      if (response.data.success) {
        setStages(response.data.stages);
        setTotalPages(response.data.pages);
        setTotal(response.data.total);
      }
    } catch (error) {
      console.error('Failed to fetch lifecycle stages:', error);
      toast.error('Failed to load lifecycle stages');
    } finally {
      setLoading(false);
    }
  }, [token, page, search, includeInactive]);

  useEffect(() => {
    fetchStages();
  }, [fetchStages]);

  const handleCreate = async () => {
    if (!formData.name.trim()) {
      toast.error('Stage name is required');
      return;
    }
    
    setSaving(true);
    try {
      const response = await axios.post(`${API}/master/lifecycle-stages?token=${token}`, formData);
      if (response.data.success) {
        toast.success('Lifecycle stage created successfully');
        setShowCreateDialog(false);
        resetForm();
        fetchStages();
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to create lifecycle stage';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!formData.name.trim()) {
      toast.error('Stage name is required');
      return;
    }
    
    setSaving(true);
    try {
      const response = await axios.put(
        `${API}/master/lifecycle-stages/${selectedStage.id}?token=${token}`,
        formData
      );
      if (response.data.success) {
        toast.success('Lifecycle stage updated successfully');
        setShowEditDialog(false);
        setSelectedStage(null);
        resetForm();
        fetchStages();
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update lifecycle stage';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      const response = await axios.delete(
        `${API}/master/lifecycle-stages/${selectedStage.id}?token=${token}`
      );
      if (response.data.success) {
        toast.success('Lifecycle stage deleted successfully');
        setShowDeleteDialog(false);
        setSelectedStage(null);
        fetchStages();
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to delete lifecycle stage';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleSetDefault = async (stage) => {
    try {
      const response = await axios.post(
        `${API}/master/lifecycle-stages/${stage.id}/set-default?token=${token}`
      );
      if (response.data.success) {
        toast.success(response.data.message);
        fetchStages();
      }
    } catch (error) {
      toast.error('Failed to set default stage');
    }
  };

  const handleToggleActive = async (stage) => {
    try {
      const response = await axios.put(
        `${API}/master/lifecycle-stages/${stage.id}?token=${token}`,
        { is_active: !stage.is_active }
      );
      if (response.data.success) {
        toast.success(`Stage ${stage.is_active ? 'deactivated' : 'activated'}`);
        fetchStages();
      }
    } catch (error) {
      toast.error('Failed to update stage');
    }
  };

  const openEditDialog = (stage) => {
    setSelectedStage(stage);
    setFormData({
      name: stage.name,
      description: stage.description || '',
      sort_order: stage.sort_order || 0,
      is_active: stage.is_active
    });
    setShowEditDialog(true);
  };

  const openDeleteDialog = (stage) => {
    setSelectedStage(stage);
    setShowDeleteDialog(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      sort_order: 0,
      is_active: true
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Stage colors based on sort order
  const getStageColor = (sortOrder) => {
    const colors = [
      'bg-slate-100 text-slate-700',
      'bg-blue-100 text-blue-700',
      'bg-purple-100 text-purple-700',
      'bg-amber-100 text-amber-700',
      'bg-green-100 text-green-700',
      'bg-emerald-100 text-emerald-700',
      'bg-teal-100 text-teal-700',
    ];
    return colors[Math.min(sortOrder - 1, colors.length - 1)] || colors[0];
  };

  return (
    <div className="p-6 space-y-6" data-testid="lifecycle-stage-management">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Layers className="h-6 w-6 text-purple-500" />
            Lifecycle Stage Management
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            Manage lifecycle stages for leads in your CRM
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={syncFromHubSpot}
            variant="outline"
            disabled={syncing}
            className="border-orange-300 text-orange-600 hover:bg-orange-50"
            data-testid="sync-hubspot-btn"
          >
            {syncing ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Sync from HubSpot
          </Button>
          <Button
            onClick={() => {
              resetForm();
              setShowCreateDialog(true);
            }}
            className="bg-purple-600 hover:bg-purple-700"
            data-testid="create-stage-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Stage
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search stages..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={includeInactive}
                onCheckedChange={setIncludeInactive}
                id="include-inactive"
              />
              <Label htmlFor="include-inactive" className="text-sm text-slate-600 dark:text-slate-400">
                Include Inactive
              </Label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stage Flow Visualization */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Lifecycle Flow</CardTitle>
          <CardDescription>
            Visual representation of your lead lifecycle stages
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-2">
            {stages.filter(s => s.is_active).map((stage, index) => (
              <div key={stage.id} className="flex items-center">
                <Badge 
                  variant="secondary" 
                  className={`${getStageColor(stage.sort_order)} px-3 py-1`}
                >
                  {stage.name}
                </Badge>
                {index < stages.filter(s => s.is_active).length - 1 && (
                  <ChevronRight className="h-4 w-4 mx-1 text-slate-400" />
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Stage List */}
      <Card>
        <CardHeader>
          <CardTitle>Lifecycle Stages ({total})</CardTitle>
          <CardDescription>
            Configure the stages that leads progress through in your sales funnel
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
            </div>
          ) : stages.length === 0 ? (
            <div className="text-center py-12">
              <Layers className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No lifecycle stages found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-slate-700">
                    <th className="text-left py-3 px-4 font-medium text-slate-600 dark:text-slate-400">Stage</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-600 dark:text-slate-400">Description</th>
                    <th className="text-center py-3 px-4 font-medium text-slate-600 dark:text-slate-400">Order</th>
                    <th className="text-center py-3 px-4 font-medium text-slate-600 dark:text-slate-400">Status</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-600 dark:text-slate-400">Created</th>
                    <th className="text-right py-3 px-4 font-medium text-slate-600 dark:text-slate-400">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {stages.map((stage) => (
                    <tr
                      key={stage.id}
                      className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50"
                      data-testid={`stage-row-${stage.id}`}
                    >
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <Badge 
                            variant="secondary" 
                            className={getStageColor(stage.sort_order)}
                          >
                            {stage.name}
                          </Badge>
                          {stage.is_default && (
                            <Badge variant="secondary" className="bg-amber-100 text-amber-700">
                              <Star className="h-3 w-3 mr-1" />
                              Default
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-slate-600 dark:text-slate-400 max-w-xs truncate">
                        {stage.description || '-'}
                      </td>
                      <td className="py-3 px-4 text-center text-slate-600 dark:text-slate-400">
                        {stage.sort_order}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <Badge
                          variant={stage.is_active ? 'default' : 'secondary'}
                          className={stage.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}
                        >
                          {stage.is_active ? (
                            <><CheckCircle className="h-3 w-3 mr-1" /> Active</>
                          ) : (
                            <><AlertCircle className="h-3 w-3 mr-1" /> Inactive</>
                          )}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-slate-600 dark:text-slate-400">
                        {formatDate(stage.created_at)}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-end gap-2">
                          {!stage.is_default && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleSetDefault(stage)}
                              title="Set as default"
                            >
                              <Star className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleActive(stage)}
                            title={stage.is_active ? 'Deactivate' : 'Activate'}
                          >
                            {stage.is_active ? (
                              <AlertCircle className="h-4 w-4" />
                            ) : (
                              <CheckCircle className="h-4 w-4" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(stage)}
                            data-testid={`edit-stage-${stage.id}`}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          {!stage.is_default && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openDeleteDialog(stage)}
                              className="text-red-500 hover:text-red-700"
                              data-testid={`delete-stage-${stage.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
              <p className="text-sm text-slate-500">
                Showing page {page} of {totalPages}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Lifecycle Stage</DialogTitle>
            <DialogDescription>
              Add a new stage to your lead lifecycle funnel
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Stage Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Marketing Qualified Lead"
                data-testid="stage-name-input"
              />
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional description..."
                rows={2}
              />
            </div>
            <div>
              <Label htmlFor="sort_order">Sort Order</Label>
              <Input
                id="sort_order"
                type="number"
                value={formData.sort_order}
                onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                min={0}
              />
              <p className="text-xs text-slate-500 mt-1">
                Lower numbers appear earlier in the lifecycle flow
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                id="is_active"
              />
              <Label htmlFor="is_active">Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={saving} data-testid="save-stage-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create Stage
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Lifecycle Stage</DialogTitle>
            <DialogDescription>
              Update the stage details
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-name">Stage Name *</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Marketing Qualified Lead"
              />
            </div>
            <div>
              <Label htmlFor="edit-description">Description</Label>
              <Textarea
                id="edit-description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional description..."
                rows={2}
              />
            </div>
            <div>
              <Label htmlFor="edit-sort_order">Sort Order</Label>
              <Input
                id="edit-sort_order"
                type="number"
                value={formData.sort_order}
                onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                min={0}
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                id="edit-is_active"
              />
              <Label htmlFor="edit-is_active">Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdate} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Lifecycle Stage</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{selectedStage?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={saving}
              data-testid="confirm-delete-btn"
            >
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete Stage
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
